import { Link, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useAuth } from "./auth";
import { AccountPage } from "./pages/AccountPage";
import { AdminPage } from "./pages/AdminPage";
import { CalibrateMonitorPage } from "./pages/CalibrateMonitorPage";
import { ChangePasswordPage } from "./pages/ChangePasswordPage";
import { DataCleanupPage } from "./pages/DataCleanupPage";
import { DisplayWindow } from "./pages/DisplayWindow";
import { HomePage } from "./pages/HomePage";
import { HomeTestPage } from "./pages/HomeTestPage";
import { LoginPage } from "./pages/LoginPage";
import { PatientPage } from "./pages/PatientPage";
import { PatientsPage } from "./pages/PatientsPage";
import { ExperimentsPage } from "./pages/ExperimentsPage";
import { ProgramsPage } from "./pages/ProgramsPage";
import { ResultsPage } from "./pages/ResultsPage";
import { SessionConsole } from "./pages/SessionConsole";
import { SessionSetup } from "./pages/SessionSetup";
import { SetupPage } from "./pages/SetupPage";
import { StationsPage } from "./pages/StationsPage";

export const APP_VERSION = "0.1.0";

export function App() {
  const { user, loading, logout } = useAuth();
  const loc = useLocation();

  if (loading) return <div className="page">Loading…</div>;
  // The patient display window is a bare, fullscreen page.
  const bare = loc.pathname.startsWith("/display/");
  if (!user) return <LoginPage />;
  if (user.must_change_password && loc.pathname !== "/password") return <Navigate to="/password" replace />;
  if (!user.must_change_password && user.must_setup_2fa && loc.pathname !== "/account" && loc.pathname !== "/password") {
    return <Navigate to="/account" replace />;
  }

  const nav = (
    <header className="topbar">
      <Link to="/" className="brand">visualTools-lite</Link>
      <nav>
        {user.role !== "patient" && <Link to="/patients">Patients</Link>}
        {user.role !== "patient" && <Link to="/programs">Programs</Link>}
        {user.role !== "patient" && <Link to="/experiments">Experiments</Link>}
        {(user.role === "admin" || user.role === "operator") && <Link to="/stations">Stations</Link>}
        {user.role === "admin" && <Link to="/admin">Admin</Link>}
      </nav>
      <span className="who">
        <Link to="/account">{user.display_name || user.username}</Link> ({user.role})
        <button className="link" onClick={() => void logout()}>Sign out</button>
      </span>
    </header>
  );

  // A page this role cannot use (an address left from someone else's session, or typed)
  // goes to the start page instead of showing a page of errors.
  const home = <Navigate to="/" replace />;
  const staffOnly = (el: React.ReactElement) => (user.role === "patient" ? home : el);
  const adminOnly = (el: React.ReactElement) => (user.role === "admin" ? el : home);

  const routes = (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/password" element={<ChangePasswordPage />} />
      <Route path="/account" element={<AccountPage />} />
      <Route path="/patients" element={staffOnly(<PatientsPage />)} />
      <Route path="/patients/:id" element={<PatientPage />} />
      <Route path="/patients/:id/new-session" element={user.role === "patient" ? <HomeTestPage /> : <SessionSetup />} />
      <Route path="/setup" element={user.role === "patient" ? <SetupPage /> : <Navigate to="/" replace />} />
      <Route path="/sessions/:id/run" element={<SessionConsole />} />
      <Route path="/sessions/:id" element={<ResultsPage />} />
      <Route path="/programs" element={staffOnly(<ProgramsPage />)} />
      <Route path="/experiments" element={staffOnly(<ExperimentsPage />)} />
      <Route path="/stations" element={<StationsPage />} />
      <Route path="/displays/:id/calibrate" element={<CalibrateMonitorPage target="display" />} />
      <Route path="/patients/:id/calibrate" element={<CalibrateMonitorPage target="patient" />} />
      <Route path="/display/:sessionId" element={<DisplayWindow />} />
      <Route path="/admin" element={adminOnly(<AdminPage />)} />
      <Route path="/admin/data" element={adminOnly(<DataCleanupPage />)} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );

  if (bare) return routes;
  return (
    <>
      {nav}
      <main className="page">{routes}</main>
      <footer className="footer">v{APP_VERSION}</footer>
    </>
  );
}
